import React, { lazy, useEffect, useState } from 'react';

import Button from '../../../../library/button/button';
import Collapsible from '../../../../library/collapsible/collapsible';
import { SHOP_TIRES } from '../../../../library/fitment/fitment-constants';
import Typography from '../../../../library/typography/typography';
import FitmentSearchCtas from './components/fitment-search-ctas/fitment-search-ctas';
import BackButton from '../../../back-button/back-button';
import NeedAssistance from '../need-assistance/need-assistance';

import {
  useFeatureFlags,
  useNavigateTo,
  useProductSearchNavigation
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import { Fitment } from '../../../../shared/types/fitment';
import { FEATURE_FLAGS } from '../../../../shared/constants/feature-flags';
import { ATV_VEHICLE_TYPE } from '../../../../shared/constants/fitment';
import { SEARCH_TYPES } from '../../../../shared/constants/search-types';

import './fitment-results.scss';

const FitmentShopTiresFlow = lazy(
  () => import('../../../fitment-shop-tires-flow/fitment-shop-tires-flow')
);

interface FitmentResultsProps {
  continueTo?: string;
  fitment: Fitment;
  handleStartTreadwell?: () => void;
  headerText?: string;
  hideNeedAssistance?: boolean;
  isEditing?: boolean;
  isShopProductsCollapsible?: boolean;
  onClose?: () => void;
  onEditingCompleted?: () => void;
  renderAboveFitmentSearchCtas?: () => void;
  searchTypeToHide?: string;
  shouldDisplayShopTiresModal?: boolean;
}

const showChooseTireCategoryAndSearchCta = [
  SHOP_TIRES.CHOOSE_TIRE_CATEGORY,
  SHOP_TIRES.SEARCH_CTAS
];

function FitmentResults(props: FitmentResultsProps) {
  const {
    continueTo,
    fitment,
    handleStartTreadwell,
    headerText,
    hideNeedAssistance,
    isEditing,
    isShopProductsCollapsible,
    onClose,
    onEditingCompleted,
    renderAboveFitmentSearchCtas,
    searchTypeToHide,
    shouldDisplayShopTiresModal
  } = props;

  const { featureFlags } = useFeatureFlags();
  const isFitmentRedesign = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_FITMENT_REDESIGN__]
  );

  const [shopTiresViewState, setShopTiresViewState] = useState(
    SHOP_TIRES.LOADING
  );

  const {
    isTireCategoriesDisplayed,
    onProductTypeCleared,
    onSearchTypeSelected,
    onShopAllTires,
    selectedSearchType,
    showBackButton
  } = useProductSearchNavigation({ fitment });
  const navigateTo = useNavigateTo();

  useEffect(() => {
    const context = window.location.hash.includes('fitment') ? 'modal' : 'page';
    trackAction(`shop_${context}_shop_products`);

    if (continueTo) {
      let url;
      switch (continueTo) {
        case SEARCH_TYPES.TIRE_GUIDE:
          trackAction('shop_modal_products_select_tires');
          url = '#/fitment/shop-tires';
          break;
        case SEARCH_TYPES.WHEELS:
          url = `/fitmentresult/wheels${fitment?.isStaggered ? '/sets' : ''}`;
          break;
        case SEARCH_TYPES.ACCESSORIES:
          onSearchTypeSelected(SEARCH_TYPES.ACCESSORIES);
          break;
      }

      if (url) {
        navigateTo(url);
      }
    }
  }, [continueTo, fitment?.isStaggered, navigateTo, onSearchTypeSelected]);

  return (
    <div className="fitment-results" styleName="results">
      {!(selectedSearchType === SEARCH_TYPES.ACCESSORIES && continueTo) &&
        (isEditing || continueTo || showBackButton) && (
          <div styleName="continue-to">
            {(isEditing || continueTo) && (
              <Button
                isPrimary
                onClick={() => {
                  if (isEditing && typeof onEditingCompleted === 'function') {
                    onEditingCompleted();
                  } else if (continueTo === SEARCH_TYPES.TIRES) {
                    onShopAllTires();
                  } else if (continueTo === SEARCH_TYPES.BUILD_YOUR_OWN) {
                    navigateTo(
                      fitment?.isStaggered
                        ? '#/package/build'
                        : '#/package/entry'
                    );
                  } else if (continueTo === SEARCH_TYPES.CLOSE) {
                    onClose?.();
                  } else {
                    onSearchTypeSelected(continueTo);
                  }
                }}
              >
                Continue
              </Button>
            )}
            {showBackButton && (
              <BackButton onClick={onProductTypeCleared} text="go back" />
            )}
          </div>
        )}

      {!isEditing &&
        (!showChooseTireCategoryAndSearchCta.includes(shopTiresViewState) ||
          fitment?.type === ATV_VEHICLE_TYPE) &&
        (!continueTo ||
          (continueTo && selectedSearchType === SEARCH_TYPES.ACCESSORIES)) && (
          <>
            {renderAboveFitmentSearchCtas?.()}
            {isShopProductsCollapsible && (
              <Collapsible
                label={headerText ?? 'What are you shopping for?'}
                styleName="shop-products-collapsible"
                toggleType="CHEVRON"
              >
                {isTireCategoriesDisplayed ? (
                  <FitmentShopTiresFlow
                    fitment={fitment}
                    handleBackClick={onProductTypeCleared}
                    setViewState={setShopTiresViewState}
                    showTopBackLink
                    viewState={shopTiresViewState}
                  />
                ) : (
                  <FitmentSearchCtas
                    onSearchTypeSelected={onSearchTypeSelected}
                    searchTypeToHide={searchTypeToHide}
                    selectedSearchType={selectedSearchType}
                    shouldDisplayShopTiresModal={shouldDisplayShopTiresModal}
                  />
                )}
                <div styleName="schedule-appointment-button-container">
                  <NeedAssistance />
                </div>
              </Collapsible>
            )}
            {!isShopProductsCollapsible && (
              <>
                {isTireCategoriesDisplayed ? (
                  <FitmentShopTiresFlow
                    fitment={fitment}
                    handleBackClick={() => onProductTypeCleared()}
                    handleStartTreadwell={handleStartTreadwell}
                    setViewState={setShopTiresViewState}
                    showTopBackLink={fitment?.type === ATV_VEHICLE_TYPE}
                    viewState={shopTiresViewState}
                  />
                ) : (
                  <>
                    <Typography
                      className="fitment-title"
                      styleName="title"
                      variant={isFitmentRedesign ? 'h3' : 'h4'}
                    >
                      {headerText ?? 'What are you shopping for?'}
                    </Typography>
                    <FitmentSearchCtas
                      onSearchTypeSelected={onSearchTypeSelected}
                      searchTypeToHide={searchTypeToHide}
                      selectedSearchType={selectedSearchType}
                      shouldDisplayShopTiresModal={shouldDisplayShopTiresModal}
                    />
                  </>
                )}
                {!hideNeedAssistance && (
                  <div styleName="schedule-appointment-button-container">
                    <NeedAssistance />
                  </div>
                )}
              </>
            )}
          </>
        )}
      {!isEditing &&
        showChooseTireCategoryAndSearchCta.includes(shopTiresViewState) &&
        fitment?.type !== ATV_VEHICLE_TYPE &&
        !continueTo && (
          <FitmentShopTiresFlow
            fitment={fitment}
            handleBackClick={() => onProductTypeCleared()}
            setViewState={setShopTiresViewState}
            showTopBackLink
            viewState={shopTiresViewState}
          />
        )}
    </div>
  );
}

export default FitmentResults;
